import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/** Id of the clustered GeoJSON source holding every store. */
const SOURCE_ID = 'stores';

/** Ids of the three layers rendered from {@link SOURCE_ID}. */
const CLUSTER_LAYER_ID = 'clusters';
const CLUSTER_COUNT_LAYER_ID = 'cluster-count';
const POINT_LAYER_ID = 'unclustered-point';

/** Name the pin image is registered under in the map's sprite. */
const PIN_IMAGE = 'store-pin';

/**
 * The pin image is requested at 2x and registered at `pixelRatio: 2`, so a 64px-wide
 * source renders as a crisp 32px pin on both standard and retina displays.
 */
const PIN_PIXEL_RATIO = 2;

const DEFAULT_CLUSTER_RADIUS = 50;
const DEFAULT_CLUSTER_MAX_ZOOM = 14;

/**
 * @typedef {Object} Store
 * @property {string|number} id - The store ID.
 * @property {string} [name] - The store name.
 * @property {string} [address_line_1] - The street address.
 * @property {string} [postal_code] - The postal/zip code.
 * @property {string} [city] - The city name.
 * @property {number} [latitude] - The latitude coordinate.
 * @property {number} [longitude] - The longitude coordinate.
 * @property {string} [phone] - The phone number.
 * @property {string} [opening_hours] - The opening hours.
 * @property {string[]} [tags] - Tags associated with the store.
 */

/**
 * Store Locator custom element that displays stores on a map and in a list/grid.
 *
 * @fires store-locator:select - When a store is selected from the list or map
 *
 * @typedef {Object} StoreLocatorRefs
 * @property {HTMLElement} [mapContainer] - The map container element.
 * @property {HTMLElement} listContainer - The list/grid container element.
 * @property {HTMLScriptElement} storeData - The JSON data script element.
 * @property {HTMLInputElement} [searchInput] - The search input element.
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements.
 * @property {HTMLElement} [emptyState] - Empty state message element.
 * @property {HTMLElement} [filtersContainer] - Container for tag filter buttons.
 *
 * @extends {Component<StoreLocatorRefs>}
 */
class StoreLocatorMap extends Component {
  requiredRefs = ['listContainer', 'storeData'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {*} */
  #map = null;

  /** @type {*} Single shared popup for the currently selected store. */
  #popup = null;

  /** @type {boolean} Whether the clustered source and its layers are on the map. */
  #clusterReady = false;

  /** @type {boolean} Whether the layer-scoped map event handlers have been bound. */
  #layerEventsBound = false;

  /** @type {Store[]} */
  #stores = [];

  /** @type {Store[]} */
  #filteredStores = [];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {HTMLElement[]} */
  #storeItems = [];

  /** @type {Set<string>} */
  #activeTags = new Set();

  /**
   * Gets the Mapbox GL JS library from window.
   * @returns {*}
   */
  get #mapboxgl() {
    return /** @type {*} */ (window).mapboxgl;
  }

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#parseData();
      this.#initSearch();
      this.#initFilters();
      this.#initStoreItems();

      if (this.#shouldShowMap()) {
        this.#observeVisibility();
      }
    } catch (error) {
      console.warn('Store locator initialization failed:', error);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#visibilityObserver?.disconnect();
    this.#popup?.remove();
    this.#map?.remove();

    super.disconnectedCallback();
  }

  /**
   * Called when the section is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    try {
      this.#parseData();
      this.#initStoreItems();

      if (this.#map) {
        // Rebuilds rather than just re-feeding the source, so cluster and pin setting
        // changes apply live. Not awaited — it handles its own errors and nothing below
        // depends on it.
        this.#refreshClusterLayers();
        this.#fitToStores(this.#stores);
      }
    } catch (error) {
      console.warn('Store locator update failed:', error);
    }
  }

  /**
   * Parses the store data from the JSON script element.
   */
  #parseData() {
    const { storeData } = this.refs;
    if (!storeData?.textContent) {
      this.#stores = [];
      this.#filteredStores = [];
      return;
    }

    try {
      this.#stores = JSON.parse(storeData.textContent);
      this.#filteredStores = [...this.#stores];
    } catch (error) {
      console.warn('Failed to parse store data:', error);
      this.#stores = [];
      this.#filteredStores = [];
    }
  }

  /**
   * Whether the map should be shown.
   * @returns {boolean}
   */
  #shouldShowMap() {
    return Boolean(this.dataset.mapboxToken && this.refs.mapContainer);
  }

  /**
   * Observes when the map container becomes visible to lazy-load the map.
   */
  #observeVisibility() {
    const { mapContainer } = this.refs;
    if (!mapContainer) return;

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          this.#initMap();
          this.#visibilityObserver?.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    this.#visibilityObserver.observe(mapContainer);
  }

  /**
   * Initializes the Mapbox map.
   */
  #initMap() {
    const token = this.dataset.mapboxToken;
    const mapboxgl = this.#mapboxgl;

    if (!token || typeof mapboxgl === 'undefined') {
      console.warn('Mapbox GL JS not loaded or token missing');
      return;
    }

    try {
      mapboxgl.accessToken = token;

      this.#map = new mapboxgl.Map({
        container: this.refs.mapContainer,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [4.9, 52.37],
        zoom: 6,
      });

      this.#map.addControl(new mapboxgl.NavigationControl());

      this.#map.on('load', async () => {
        await this.#initClusterLayers();
        this.#fitToStores(this.#stores);
      });
    } catch (error) {
      console.warn('Failed to initialize Mapbox map:', error);
    }
  }

  /**
   * Builds a GeoJSON FeatureCollection from the stores that have coordinates.
   * @param {Store[]} stores - Stores to convert.
   * @returns {*} GeoJSON FeatureCollection.
   */
  #buildFeatureCollection(stores) {
    return {
      type: 'FeatureCollection',
      features: stores
        .filter((store) => store.latitude && store.longitude)
        .map((store) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [store.longitude, store.latitude] },
          properties: { id: String(store.id) },
        })),
    };
  }

  /**
   * Registers an image in the map's sprite under {@link PIN_IMAGE}.
   *
   * Uses a plain `Image` rather than `map.loadImage()`: Mapbox's own loader decodes
   * the response as a raw buffer without an image MIME type, which fails for the SVG
   * default pin. An `<img>` respects the Content-Type (and data URIs) instead.
   * @param {string} url - Image URL or data URI.
   * @returns {Promise<boolean>} Whether the image loaded.
   */
  #loadPinImage(url) {
    return new Promise((resolve) => {
      if (this.#map.hasImage(PIN_IMAGE)) {
        resolve(true);
        return;
      }

      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (!this.#map.hasImage(PIN_IMAGE)) this.#map.addImage(PIN_IMAGE, image, { pixelRatio: PIN_PIXEL_RATIO });
        resolve(true);
      };
      image.onerror = () => resolve(false);
      image.src = url;
    });
  }

  /**
   * Loads the merchant's pin image, falling back to the section's default pin when they
   * haven't uploaded one — or when the uploaded one fails to load.
   * @returns {Promise<void>}
   */
  async #loadPinImages() {
    const url = this.dataset.pinImage;
    const fallback = this.dataset.defaultPin;

    if (url && (await this.#loadPinImage(url))) return;
    if (url) console.warn(`Store locator: falling back to the default pin, "${url}" failed to load`);
    if (fallback) await this.#loadPinImage(fallback);
  }

  /**
   * Adds the clustered source, its layers, and their event handlers to the map.
   * @returns {Promise<void>}
   */
  async #initClusterLayers() {
    if (!this.#map || this.#clusterReady) return;

    try {
      await this.#loadPinImages();
      this.#addClusterLayers();
      this.#bindLayerEvents();
      this.#clusterReady = true;
    } catch (error) {
      console.warn('Failed to initialize store clustering:', error);
    }
  }

  /**
   * Tears the clustered source, its layers, and the pin image down and rebuilds them.
   * This is what makes theme-editor edits apply live: Mapbox can't change
   * clusterRadius/clusterMaxZoom on an existing source, and the pin image lives in the
   * map's sprite, which outlives the layers that reference it.
   * @returns {Promise<void>}
   */
  async #refreshClusterLayers() {
    if (!this.#map || !this.#clusterReady) return;

    this.#clusterReady = false;

    try {
      for (const id of [CLUSTER_LAYER_ID, CLUSTER_COUNT_LAYER_ID, POINT_LAYER_ID]) {
        if (this.#map.getLayer(id)) this.#map.removeLayer(id);
      }
      if (this.#map.getSource(SOURCE_ID)) this.#map.removeSource(SOURCE_ID);

      if (this.#map.hasImage(PIN_IMAGE)) this.#map.removeImage(PIN_IMAGE);

      await this.#loadPinImages();
      this.#addClusterLayers();
      this.#clusterReady = true;
    } catch (error) {
      console.warn('Failed to refresh store clustering:', error);
    }
  }

  /**
   * Adds the clustered GeoJSON source and the cluster, count, and pin layers.
   */
  #addClusterLayers() {
    const clusterRadius = Number(this.dataset.clusterRadius) || DEFAULT_CLUSTER_RADIUS;
    const clusterMaxZoom = Number(this.dataset.clusterMaxZoom) || DEFAULT_CLUSTER_MAX_ZOOM;

    // Mapbox's paint properties only accept hex or legacy comma-separated colors — not
    // the space-separated `rgb(18 18 18 / 1)` the theme's --color-* properties resolve
    // to — so the section passes the color scheme through Liquid's `color_to_hex`.
    const clusterColor = this.dataset.clusterColor || '#1a1a1a';
    const clusterTextColor = this.dataset.clusterTextColor || '#ffffff';

    this.#map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: this.#buildFeatureCollection(this.#filteredStores),
      cluster: true,
      clusterRadius,
      clusterMaxZoom,
    });

    this.#map.addLayer({
      id: CLUSTER_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': clusterColor,
        'circle-radius': ['step', ['get', 'point_count'], 18, 25, 24, 100, 32],
        'circle-stroke-color': clusterTextColor,
        'circle-stroke-width': 2,
      },
    });

    this.#map.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 12,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': clusterTextColor,
      },
    });

    this.#map.addLayer({
      id: POINT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': PIN_IMAGE,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
      },
    });
  }

  /**
   * Binds the layer-scoped click and hover handlers. Mapbox resolves these by layer id
   * at event time, so they survive {@link #refreshClusterLayers} recreating the layers
   * and must only ever be bound once.
   */
  #bindLayerEvents() {
    if (this.#layerEventsBound) return;

    this.#map.on('click', CLUSTER_LAYER_ID, (/** @type {*} */ event) => {
      const [feature] = this.#map.queryRenderedFeatures(event.point, { layers: [CLUSTER_LAYER_ID] });
      const clusterId = feature?.properties?.cluster_id;
      if (clusterId == null) return;

      this.#map.getSource(SOURCE_ID)?.getClusterExpansionZoom(clusterId, (/** @type {*} */ error, zoom) => {
        if (error) return;
        this.#map.easeTo({
          center: feature.geometry.coordinates,
          zoom,
          duration: prefersReducedMotion() ? 0 : 500,
        });
      });
    });

    this.#map.on('click', POINT_LAYER_ID, (/** @type {*} */ event) => {
      const storeId = event.features?.[0]?.properties?.id;
      if (storeId) this.#selectStore(String(storeId));
    });

    for (const layerId of [CLUSTER_LAYER_ID, POINT_LAYER_ID]) {
      this.#map.on('mouseenter', layerId, () => {
        this.#map.getCanvas().style.cursor = 'pointer';
      });
      this.#map.on('mouseleave', layerId, () => {
        this.#map.getCanvas().style.cursor = '';
      });
    }

    this.#layerEventsBound = true;
  }

  /**
   * Fits the map bounds to show the given stores.
   * @param {Store[]} stores - Stores to fit.
   */
  #fitToStores(stores) {
    const mapboxgl = this.#mapboxgl;
    if (!this.#map || !mapboxgl) return;

    const validStores = stores.filter((s) => s.latitude && s.longitude);
    if (validStores.length === 0) return;

    try {
      const bounds = new mapboxgl.LngLatBounds();

      for (const store of validStores) {
        bounds.extend([store.longitude, store.latitude]);
      }

      this.#map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 12,
        duration: prefersReducedMotion() ? 0 : 1000,
      });
    } catch (error) {
      console.warn('Failed to fit map bounds:', error);
    }
  }

  /**
   * Initializes search input.
   */
  #initSearch() {
    const { searchInput } = this.refs;
    if (searchInput) {
      searchInput.addEventListener('input', this.#handleSearch, { signal: this.#controller.signal });
    }
  }

  /**
   * Initializes tag filter buttons.
   */
  #initFilters() {
    const { filtersContainer } = this.refs;
    if (!filtersContainer) return;

    const filterButtons = filtersContainer.querySelectorAll('[data-tag]');
    filterButtons.forEach((button) => {
      button.addEventListener('click', this.#handleFilterClick, { signal: this.#controller.signal });
    });
  }

  /**
   * Handles filter button clicks.
   * @param {Event} event - The click event.
   */
  #handleFilterClick = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const tag = button.dataset.tag;
    if (!tag) return;

    const isPressed = button.getAttribute('aria-pressed') === 'true';

    if (isPressed) {
      this.#activeTags.delete(tag);
      button.setAttribute('aria-pressed', 'false');
    } else {
      this.#activeTags.add(tag);
      button.setAttribute('aria-pressed', 'true');
    }

    this.#applyFilters();
  };

  /**
   * Handles search input to filter stores.
   */
  #handleSearch = debounce(() => {
    this.#applyFilters();
  }, 300);

  /**
   * Applies both search and tag filters to the store list.
   */
  #applyFilters() {
    const { searchInput } = this.refs;
    const query = searchInput?.value.toLowerCase().trim() || '';

    this.#filteredStores = this.#stores.filter((store) => {
      // Check search query
      const matchesSearch =
        !query ||
        store.name?.toLowerCase().includes(query) ||
        store.city?.toLowerCase().includes(query) ||
        store.address_line_1?.toLowerCase().includes(query) ||
        store.postal_code?.toLowerCase().includes(query);

      // Check tag filters (if any tags are active, store must have ALL of them)
      const matchesTags =
        this.#activeTags.size === 0 ||
        [...this.#activeTags].every((activeTag) => {
          return store.tags?.some((tag) => {
            const tagHandle = tag.toLowerCase().replace(/\s+/g, '-');
            return tagHandle === activeTag;
          });
        });

      return matchesSearch && matchesTags;
    });

    const filteredIds = new Set(this.#filteredStores.map((s) => String(s.id)));

    this.#updateStoreList(filteredIds);
    this.#updateClusterData();
    this.#announceResults();
  }

  /**
   * Updates the visibility of store items in the list.
   * @param {Set<string>} filteredIds - Set of visible store IDs.
   */
  #updateStoreList(filteredIds) {
    this.#storeItems.forEach((item) => {
      const storeId = item.dataset.storeId || '';
      item.hidden = !filteredIds.has(storeId);
    });

    const { emptyState } = this.refs;
    if (emptyState) {
      emptyState.hidden = this.#filteredStores.length > 0;
    }
  }

  /**
   * Feeds the filtered stores back into the clustered source so cluster counts
   * always reflect the active search and tag filters.
   */
  #updateClusterData() {
    if (!this.#map || !this.#clusterReady) return;

    this.#map.getSource(SOURCE_ID)?.setData(this.#buildFeatureCollection(this.#filteredStores));

    if (this.#filteredStores.length > 0) {
      this.#fitToStores(this.#filteredStores);
    }
  }

  /**
   * Announces search results to screen readers.
   */
  #announceResults() {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;

    const count = this.#filteredStores.length;

    if (count === 0) {
      liveRegion.textContent = this.dataset.noStoresFound || 'No stores found';
    } else {
      const template = count === 1 ? this.dataset.storesFoundOne : this.dataset.storesFoundOther;
      liveRegion.textContent = template ? template.replace('__count__', String(count)) : `${count} stores found`;
    }
  }

  /**
   * Initializes store items and click handlers (only when map is enabled).
   */
  #initStoreItems() {
    this.#storeItems = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-store-id]')]);

    // Only add click handlers when map is enabled, since selection is used to fly to stores
    if (!this.#shouldShowMap()) return;

    this.#storeItems.forEach((item) => {
      item.addEventListener(
        'click',
        () => {
          this.#selectStore(item.dataset.storeId || '');
        },
        { signal: this.#controller.signal },
      );

      item.addEventListener(
        'keydown',
        (e) => {
          const event = /** @type {KeyboardEvent} */ (e);
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.#selectStore(item.dataset.storeId || '');
          }
        },
        { signal: this.#controller.signal },
      );
    });
  }

  /**
   * Selects a store and flies to it on the map.
   * @param {string} storeId - The store ID to select.
   */
  #selectStore(storeId) {
    this.#storeItems.forEach((item) => {
      item.setAttribute('aria-current', item.dataset.storeId === storeId ? 'true' : 'false');
    });

    this.#popup?.remove();

    const store = this.#stores.find((s) => String(s.id) === storeId);
    if (store && this.#map && store.latitude && store.longitude) {
      this.#map.flyTo({
        center: [store.longitude, store.latitude],
        zoom: 14,
        duration: prefersReducedMotion() ? 0 : 1500,
      });

      // Pins are drawn by a symbol layer rather than as DOM markers, so the popup is
      // owned here instead of being attached to an individual marker.
      this.#popup = new this.#mapboxgl.Popup({ offset: 25 })
        .setLngLat([store.longitude, store.latitude])
        .setHTML(this.#popupHtml(store))
        .addTo(this.#map);
    }

    this.dispatchEvent(
      new CustomEvent('store-locator:select', {
        bubbles: true,
        detail: { store },
      }),
    );
  }

  /**
   * Builds the popup markup for a store.
   * @param {Store} store - The store to render.
   * @returns {string} HTML string.
   */
  #popupHtml(store) {
    const phone = String(store.phone || '').replace(/[^\d+()\s-]/g, '');

    return `
      <strong>${this.#escapeHtml(store.name)}</strong><br>
      ${this.#escapeHtml(store.address_line_1)}<br>
      ${this.#escapeHtml(store.postal_code)} ${this.#escapeHtml(store.city)}
      ${phone ? `<br><a href="tel:${phone}">${this.#escapeHtml(store.phone)}</a>` : ''}
    `;
  }

  /**
   * Escapes a value for interpolation into popup markup.
   * @param {string} [value] - The value to escape.
   * @returns {string}
   */
  #escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value ?? '';
    return element.innerHTML;
  }
}

if (!customElements.get('store-locator-map')) {
  customElements.define('store-locator-map', StoreLocatorMap);
}
